import React from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { gtag } from "../utils/gtag";
import { MAILTO_URL } from "../utils/constants";
import { SiteImages } from "../utils/imagePaths";
import "./Menus.css";

const Menus = () => {
  const menuItems = [
    {
      title: "Afternoon Tea",
      price: "from £25 per person",
      tagline: "A beautifully presented selection of sweet and savoury favourites, perfect for any occasion.",
      image: SiteImages.menuAfternoonTea,
      imageAlt: "Artisanal Afternoon Tea spread",
      items: [
        "A selection of freshly made finger sandwiches (4 per person)",
        "A selection of cakes and sweet treats (4 per person, with some homemade bakes)",
        "A freshly baked homemade scone with jam & cream",
        "A selection of teas, served with milk & sugar",
      ],
      additionalInfo: [
        "Includes elegant bone china tea sets and full setup at your event. Optional event staff are available at an additional charge. If you would like us to provide table styling and decorative details, please get in touch to discuss your vision.",
        "Some elements are baked fresh in-house, with everything carefully chosen for quality and flavour.",
        "We\u2019re happy to cater for dietary requirements\u2014please get in touch to discuss your needs.",
      ],
    },
    {
      title: "Classic Charcuterie",
      price: "from £18.50 per person",
      tagline: "An elegant grazing experience designed to impress, combining rich flavours with refined presentation.",
      image: SiteImages.menuCharcuterie,
      imageAlt: "Classic Charcuterie and grazing board display",
      items: [
        "A curated selection of cured meats",
        "A variety of cheeses, thoughtfully paired",
        "Fresh and dried fruits for balance and colour",
        "Crackers and freshly served bread",
        "Nuts and marinated olives",
      ],
      additionalInfo: [
        "Each graze is carefully styled to create a visually striking centrepiece, perfect for entertaining and special occasions.",
        "Pricing ranges from £18.50 per person for a lighter graze to £25 per person for a more abundant, meal-style experience.",
        "This menu is a sample. A bespoke consultation will be provided at the point of booking to curate a menu perfectly suited to your event.",
        "We\u2019re happy to cater for dietary requirements\u2014please get in touch to discuss your requirements.",
      ],
    },
  ];

  return (
    <div className="menus-container">
      <Helmet>
        <title>Our Menus | Blossom and Graze Bristol – Grazing Boards & Catering</title>
        <meta name="description" content="Explore Blossom and Graze Bristol's grazing board menus: Afternoon Tea from £25/person and Classic Charcuterie from £18.50/person. Vegan and vegetarian options available. Perfect for weddings, corporate events, and private parties in Bristol." />
        <link rel="canonical" href="https://www.blossomandgraze.co.uk/menus" />
        <meta property="og:url" content="https://www.blossomandgraze.co.uk/menus" />
        <meta property="og:title" content="Our Menus | Blossom and Graze Bristol – Grazing Boards & Catering" />
        <meta property="og:description" content="Afternoon Tea from £25/person and Classic Charcuterie from £18.50/person. Vegan and vegetarian options available. Artisanal grazing boards for every occasion in Bristol." />
      </Helmet>
      <div className="menus-header">
        <span className="menus-header-label">Artisanal Catering</span>
        <div className="menus-header-divider" />
        <h1>Our Menus</h1>
        <p>Carefully curated grazing experiences for every occasion</p>
      </div>

      <div className="menu-grid">
        {menuItems.map((menu, index) => (
          <div key={index} className="menu-card">
            <div className="menu-card-image">
              <img src={menu.image} alt={menu.imageAlt} />
            </div>
            <div className="menu-card-body">
              <div className="menu-card-header">
                <h2>{menu.title}</h2>
                <span className="price">{menu.price}</span>
              </div>
              <p className="description">{menu.tagline}</p>
              <ul className="menu-items">
                {menu.items.map((item, itemIndex) => (
                  <li key={itemIndex}>
                    <span className="material-symbols-outlined menu-item-icon">
                      spa
                    </span>
                    {item}
                  </li>
                ))}
              </ul>
              {menu.additionalInfo && menu.additionalInfo.map((para, paraIndex) => (
                <p key={paraIndex} className="menu-additional-info">{para}</p>
              ))}
              <a href={MAILTO_URL} className="menu-card-cta" onClick={() => {
                gtag("event", "enquire_click", {
                  link_location: "menus_card",
                });
              }}>
                Enquire Now
              </a>
            </div>
          </div>
        ))}
      </div>

      <div className="grazing-boxes-section">
        <div className="grazing-boxes-inner">
          <div className="grazing-boxes-image">
            <img
              src={SiteImages.menuGrazingBox}
              alt="Grazing box filled with charcuterie, cheeses, fruits and treats"
            />
          </div>
          <div className="grazing-boxes-content">
            <span className="grazing-boxes-label">Something Special</span>
            <div className="grazing-boxes-divider" />
            <h2>Grazing Boxes</h2>
            <span className="grazing-boxes-price">from £55</span>
            <p className="grazing-boxes-description">
              The perfect alternative to a grazing table — for when you want
              something truly special but on a smaller, more intimate scale.
              Each box is beautifully styled and filled with a curated
              selection of seasonal treats, ideal as a gift or for a cosy
              celebration.
            </p>
            <a
              href={MAILTO_URL}
              className="menu-card-cta grazing-boxes-cta"
              onClick={() => {
                gtag("event", "enquire_click", {
                  link_location: "grazing_boxes",
                });
              }}
            >
              Enquire Now
            </a>
          </div>
        </div>
      </div>

      <div className="grazing-boxes-section">
        <div className="grazing-boxes-inner">
          <div className="grazing-boxes-content">
            <span className="grazing-boxes-label">A Treat for Two (or More)</span>
            <div className="grazing-boxes-divider" />
            <h2>Afternoon Tea Boxes</h2>
            <div className="grazing-boxes-price-list">
              <span className="grazing-boxes-price">for 2 — £40</span>
              <span className="grazing-boxes-price">for 4 — £70</span>
            </div>
            <p className="grazing-boxes-description">
              A beautifully packaged afternoon tea experience to enjoy wherever
              you are. Each box is lovingly filled with freshly made finger
              sandwiches, homemade bakes, sweet treats, and a scone with jam
              &amp; cream — all thoughtfully presented and ready to enjoy.
              Perfect as a gift or a special celebration at home.
            </p>
            <a
              href={MAILTO_URL}
              className="menu-card-cta grazing-boxes-cta"
              onClick={() => {
                gtag("event", "enquire_click", {
                  link_location: "afternoon_tea_boxes",
                });
              }}
            >
              Enquire Now
            </a>
          </div>
          <div className="grazing-boxes-image">
            <img
              src={SiteImages.menuAfternoonTeaBox}
              alt="Afternoon tea box with finger sandwiches, scones and sweet treats"
            />
          </div>
        </div>
      </div>

      <div className="optional-extras">
        <div className="optional-extras-inner">
          <span className="optional-extras-label">A Little Extra Magic</span>
          <div className="optional-extras-divider" />
          <h2>Optional Extras</h2>
          <p className="optional-extras-intro">
            Elevate your gathering with our carefully curated finishing touches. Each addition is thoughtfully chosen to complement the beauty of your event and leave your guests with something truly memorable. Optional extras are available to add to any food booking.
          </p>
          <ul className="extras-list">
            <li>
              <span className="material-symbols-outlined extras-icon">local_florist</span>
              <div>
                <strong>Fresh Flowers</strong>
                <span>Seasonal blooms to adorn your grazing table with natural elegance</span>
              </div>
            </li>
            <li>
              <span className="material-symbols-outlined extras-icon">circle</span>
              <div>
                <strong>Balloon Hoop</strong>
                <span>A stunning balloon hoop arrangement to frame your event with a whimsical flourish</span>
              </div>
            </li>
            <li>
              <span className="material-symbols-outlined extras-icon">edit_note</span>
              <div>
                <strong>Welcome Sign</strong>
                <span>A personalised welcome sign, beautifully styled to set the tone for your occasion</span>
              </div>
            </li>
            <li>
              <span className="material-symbols-outlined extras-icon">redeem</span>
              <div>
                <strong>Favour Bags</strong>
                <span>Delightful keepsake bags filled with thoughtful treats for your guests to take home</span>
              </div>
            </li>
            <li>
              <span className="material-symbols-outlined extras-icon">local_bar</span>
              <div>
                <strong>Welcome Drinks Station</strong>
                <span>A beautifully styled non-alcoholic drinks station to welcome your guests in style — think elderflower fizz, fruit-infused waters, and seasonal refreshments (please note: we are unable to serve alcohol as we do not hold a licence)</span>
              </div>
            </li>
          </ul>
          <p className="optional-extras-note">
            Please mention any extras you&apos;d like when making your enquiry and we&apos;ll be happy to discuss pricing and availability.
          </p>
        </div>
      </div>

      <div className="menu-plan-cta">
        <div className="menu-plan-cta-inner">
          <h2>Plan Your Event</h2>
          <p>
            Whether it&apos;s an intimate garden party or a corporate gathering,
            we create bespoke grazing experiences that delight the senses.
          </p>
          <div className="menu-plan-cta-buttons">
            <a href={MAILTO_URL} className="menu-cta" onClick={() => {
              gtag("event", "enquire_click", {
                link_location: "menus_cta",
              });
            }}>
              Enquire Now
            </a>
            <Link to="/gallery" className="menu-cta-text">
              View Sample Event Gallery
            </Link>
          </div>
          <p className="menu-plan-note">
            Minimum 10 people &mdash; Locally sourced ingredients
          </p>
        </div>
      </div>
    </div>
  );
};

export default Menus;

